<script setup lang="ts">
import { ref } from "vue";
import { useTranslation } from "i18next-vue";
import AudioPlayerItem from "./AudioPlayerItem.vue";
import VoiceRecordingOverlay from "@/components/field-observations/VoiceRecordingOverlay.vue";
import CardDashedButton from "../cards/CardDashedButton.vue";
import type { ExistingMedia } from "@/composable/useFieldObservationForm";

/**
 * Notes vocales d'une observation, posées juste sous son commentaire (un
 * vocal est un commentaire dicté) : lecture et suppression de chaque
 * enregistrement, et le bouton micro qui en ajoute un (surcouche
 * d'enregistrement) — contour pointillé, comme « Ajouter des éléments ».
 * Pas de libellé de section : le bouton se suffit.
 */

defineProps<{
  recordings: ExistingMedia[];
  isUploading: boolean;
  diagnosticId: number;
}>();

const emit = defineEmits<{
  (e: "recorded", audio: Blob): void;
  (e: "delete", recordingId: number): void;
}>();

const { t } = useTranslation();

const showVoiceOverlay = ref(false);

const handleVoiceRecorded = (audio: Blob): void => {
  showVoiceOverlay.value = false;
  emit("recorded", audio);
};
</script>

<template>
  <div class="d-flex flex-column gap-1">
    <AudioPlayerItem
      v-for="audio in recordings"
      :key="audio.id"
      :src="audio.url"
      :loading="audio.isLoading"
      @delete="emit('delete', audio.id)"
    />
    <CardDashedButton
      icon="microphone"
      :label="t('canvas:observations.editor.addVoice')"
      :loading="isUploading"
      @click="showVoiceOverlay = true"
    />

    <VoiceRecordingOverlay
      v-if="showVoiceOverlay"
      :diagnostic-id="diagnosticId"
      @close="showVoiceOverlay = false"
      @recorded="handleVoiceRecorded"
    />
  </div>
</template>
